/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/**
 * Pure builders for `/safeCircleNotifications/{toUid}/{id}` payloads.
 *
 * A write here is fanned out to the recipient's FCM token by the
 * onSafeCircleNotificationCreate cloud function (functions/src/
 * safeCircleNotifications.ts). The write rule allows it for any authed user
 * as long as `fromUid` === auth.uid.
 *
 * Build 164: friend-request-SENT and new-MESSAGE paths never wrote a
 * notification (only friend-ACCEPTED did), so recipients got no push. These
 * builders centralize the shape so every notify path is consistent and
 * testable, and the `fromUid` (required by the rule) is never forgotten.
 *
 * `createdAt` is injected by the caller (RTDB serverTimestamp()) — kept out of
 * these pure builders so they stay deterministic/testable.
 */

export interface NotificationPayload {
  fromUid: string;
  title: string;
  body: string;
  type: string;
  data: Record<string, string>;
}

/** Truncate a free-text snippet so a long message body stays push-friendly. */
export function truncateForPush(text: string, max = 120): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

/** "Someone you know" style sender label, never empty. */
export function senderLabel(
  displayName?: string | null,
  email?: string | null,
): string {
  const name = displayName?.trim();
  if (name) return name;
  const handle = email?.split("@")[0]?.trim();
  if (handle) return handle;
  return "Someone you know";
}

/** Push shown to the RECIPIENT when a friend request is SENT to them. */
export function buildFriendRequestNotification(
  fromUid: string,
  senderName: string,
): NotificationPayload {
  return {
    fromUid,
    // Title names the ACTION, not the app: iOS and Android already render the
    // app name above the notification, so a "MyAmbits" title wasted the most
    // prominent line and buried what actually happened. Recipients couldn't
    // tell a friend request apart from an invite-to-join-the-app at a glance.
    title: "Friend request",
    body: `${senderName} wants to add you as a friend`,
    type: "FRIEND_REQUEST",
    data: { action: "open_friends_tab", friendUid: fromUid },
  };
}

/** Push shown to the RECIPIENT when a friend ACCEPTS their request. */
export function buildFriendAcceptedNotification(
  fromUid: string,
  accepterName: string,
): NotificationPayload {
  return {
    fromUid,
    // Same action-first shape as buildFriendRequestNotification above.
    title: "Friend request accepted",
    body: `${accepterName} is now your friend`,
    type: "FRIEND_ACCEPTED",
    data: { action: "open_friends_tab", friendUid: fromUid },
  };
}

/** Push shown to the RECIPIENT when a friend sends a message. */
export function buildMessageNotification(
  fromUid: string,
  senderName: string,
  messageText: string,
  sessionId: string,
): NotificationPayload {
  return {
    fromUid,
    title: senderName,
    body: truncateForPush(messageText),
    type: "NEW_MESSAGE",
    data: { action: "open_session", sessionId, friendUid: fromUid },
  };
}
